import styled, { css } from 'styled-components';
import { Typography } from '../ui/Typography';
import { NewRadioButtonProps } from './types';
import { FlexCenterAligned } from '../../styled/common';
import { ThemeType } from '../../styled/theme/constants';

export const RadioText = styled(Typography)<{
  color?: string;
  $isChecked: boolean;
}>`
  color: ${({ color, $isChecked, theme }) =>
    $isChecked ? color : theme.colors.text.primary};
`;

export const Mark = styled.span`
  position: relative;
  display: inline-block;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
  box-sizing: border-box;

  ${({ theme: { mode } }) =>
    mode === ThemeType.dark
      ? css`
          width: 9px;
          height: 9px;
        `
      : css`
          width: 11px;
          height: 11px;
        `};
`;

export const MarkContainer = styled.span<{ $isChecked: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme: { mode } }) =>
    mode === ThemeType.dark
      ? css`
          width: 16px;
          height: 16px;
          border: 1px solid rgba(239, 240, 244, 0.4);
          background-color: rgba(225, 227, 234, 0.2);
        `
      : css`
          width: 20px;
          height: 20px;
          border: 1px solid #ededed;
        `};

  border-radius: 50%;

  transition: all 0.2s ease-in-out;
  box-sizing: border-box;

  ${({ $isChecked, theme }) => {
    return (
      $isChecked &&
      css`
        border: 1px solid ${theme.colors.systemColors.blue};
        background-color: inherit;

        ${Mark} {
          background-color: ${theme.colors.systemColors.blue};
        }
      `
    );
  }}
`;

export const RadioContainer = styled.label<
  Pick<NewRadioButtonProps, 'markerPosition'>
>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 17.5px 15px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  cursor: pointer;

  ${({ markerPosition }) =>
    markerPosition === 'left' &&
    css`
      flex-direction: row-reverse;
      justify-content: flex-end;
      gap: 5px;
    `}

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    width: calc(100% - 15px);
    height: 0.5px;
    background-color: ${({ theme: { mode, colors } }) =>
      mode === ThemeType.dark ? 'inherit' : colors.background.secondary};
  }

  &:hover {
    ${RadioText} {
      color: ${({ theme }) => theme.colors.systemColors.blue};
    }

    ${MarkContainer} {
      border: 1px solid ${({ theme }) => theme.colors.systemColors.blue};
    }
  }

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }

  @media (max-width: ${({ theme }) => theme.tabletBreakpoint}) {
    ${({ theme: { mode }, $isChecked }) =>
      mode === ThemeType.dark && $isChecked
        ? css`
            background-color: #292929;
            border-radius: 14px;
          `
        : ''};
  }

  -webkit-tap-highlight-color: transparent;
`;

export const RadioInput = styled.input`
  opacity: 0;
  visibility: hidden;
  position: absolute;
  z-index: -1;
`;

export const RadioLabel = styled(FlexCenterAligned)`
  box-sizing: border-box;
  padding: 5px 8px;
  gap: 10px;
  border-radius: 10px;
  border: 1px solid #bae3cb;
  background-color: #e7f7ed;
`;

export const RadioLabelText = styled(Typography)`
  color: #4ca049;
  font-size: 12px;
`;
